import { lazy, Suspense, useEffect, useMemo, useRef, useState } from "react";
import { Box, Typography } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { Redirect } from "react-router-dom";
import { showAlert } from "../../../globalStore/slices/alert/alertSlice";
import {
  updateFacilities,
  removeFacility,
} from "../../../globalStore/slices/organization/organizationSlice";
import { useLocation } from "react-router-dom";
import AddIcon from '@mui/icons-material/Add';
import AssignmentReturnedIcon from '@mui/icons-material/AssignmentReturned';
import ConfirmationModalContent from "../../../components/Modals/ConfirmationModalContent";
import CreateFacility from "./CreateFacility/CreateFacility";
import EditFacility from "./EditFacility/EditFacility";
import EditGeofence from "./EditGeofence/EditGeofence";
import FacilityDetail from "./FacilityDetail/FacilityDetail";
import FacilityTable from "./FacilityTable/FacilityTable";
import HoverIconButton from "../../../components/ReusedComponents/HoverIconButton";
import Loading from "../../../components/Loading/Loading";
import MapIcon from '@mui/icons-material/Map';
import ModalDialog from "../../../components/Modals/ModalDialog/ModalDialog";
import PageLayout from "../../../components/Layout/PageLayout";
import BusinessIcon from '@mui/icons-material/Business';

// Component uses xlxs which is very heavy, which is why we are using lazy/Suspense
const ImportLocations = lazy(() => import("./ImportLocations/ImportLocations"));

export default function Facilities(props) {
  const {
    apiUrl,
    facilities,
    dispatchGlobal,
    organization,
    permissions = {},
    setView = {},
    token,
  } = props;
  const [state, setState] = useState({
    facilities: [],
  });
  const [dialog, setDialog] = useState({
    dialogTitle: "",
    dialogShow: false,
    selectedLocation: {},
  });
  const [redirect, setRedirect] = useState({
    isRedirect: false,
    selectedFacility: "",
  });

  const mounted = useRef(false);
  // first render useEffect, retrieve facilities; retrieve facilities function is memoized via useCallback so it can be used in the useEffect dep array without creating an infinite loop
  useEffect(() => {
    const { state: routerState = {} } = props.location;
    const { modalShow = false, modal = "" } = routerState;

    setState((prevState) => {
      return {
        ...prevState,
        facilities: Object.keys(facilities).map((k) => facilities[k]),
      };
    });

    if (modalShow) {
      setDialog({ dialogTitle: modal, dialogShow: modalShow });
    }

    mounted.current = true;
    // This controls the quickLinks and modal behavior
    return props.history.listen(() => {
      setDialog({ dialogTitle: modal, dialogShow: modalShow });
    });
  }, [facilities, props.history, props.location]);

  const handleDelete = (id) => {
    fetch(`${apiUrl}facilities/${id}`, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        "auth-token": token,
      },
      body: JSON.stringify({ deleteImages: true }),
    })
      .then((response) => response.json())
      .then((res) => {
        if (res.success) {
          //invoke removeFacility to update the store
          dispatchGlobal(removeFacility({ facilityId: id }));
          handleConfirmationModal("Facility Successfully Deleted");
          dialogClose();
        }
      })
      .catch(() => {
        handleConfirmationModal("There was a problem", true);
      });
  };

  const handleConfirmationModal = (text, error) => {
    dispatchGlobal(
      showAlert({ type: error ? "error" : "success", text: text })
    );
  };

  const dialogClose = () =>
    setDialog({
      ...dialog,
      dialogShow: false,
      dialogTitle: "",
      selectedLocation: {},
    });

  const { dialogShow, selectedLocation, dialogTitle } = dialog;
  const { propertiesMap = {} } = organization;
  const { locationTypes = [] } = propertiesMap;

  function useQuery() {
    const { search } = useLocation();

    return useMemo(() => new URLSearchParams(search), [search]);
  }

  let query = useQuery();
  const enableFeatures = query.get("enableFeatures") || false;

  const switchDialog = (dialog) => {
    switch (dialog) {
      case "Create Facility":
        return (
          <CreateFacility
            apiUrl={apiUrl}
            dispatchGlobal={dispatchGlobal}
            hasFormFields={true}
            locationTypes={locationTypes}
            notificationModal={handleConfirmationModal}
            onHide={dialogClose}
            token={token}
            updateFacilities={updateFacilities}
          />
        );
      case "Delete Facility":
        return (
          <ConfirmationModalContent
            content={`Are you sure you want to delete ${selectedLocation.name}? This would result in ALL associated uploaded images being deleted. This action cannot be
            undone.`}
            handleSubmit={() => {
              handleDelete(selectedLocation.facilityId);
            }}
            handleCancel={() => {
              dialogClose();
            }}
          />
        );
      case "Edit Facility":
        return (
          <EditFacility
            apiUrl={apiUrl}
            dispatchGlobal={dispatchGlobal}
            facility={selectedLocation}
            hasFormFields={true}
            locationTypes={locationTypes}
            match={{
              params: { facilityId: selectedLocation.facilityId },
            }}
            notificationModal={handleConfirmationModal}
            onHide={dialogClose}
            onSuccess={() => console.log("YOU NEED TO WORK ON THIS")}
            token={token}
            updateFacilities={updateFacilities}
          />
        );
      case "Edit Geofence":
        return (
          enableFeatures ?
            <EditGeofence
              apiUrl={apiUrl}
              dispatchGlobal={dispatchGlobal}
              facility={selectedLocation}
              handleConfirmationModal={handleConfirmationModal}
              onHide={dialogClose}
              token={token}
              updateFacilities={updateFacilities}
            /> : null
        )
      case "Facility Detail":
        return (
          <FacilityDetail
            handleClose={() => {
              dialogClose();
            }}
            open={true}
            token={token}
            apiUrl={apiUrl}
            locationTypes={locationTypes}
            facility={selectedLocation}
            notificationModal={handleConfirmationModal}
          />
        );

      case "Import Facilities":
        return (
          <Suspense fallback={<Loading />}>
            <ImportLocations
              apiUrl={apiUrl}
              locationTypes={locationTypes}
              notificationModal={handleConfirmationModal}
              onHide={dialogClose}
              onSuccess={() => console.log("YOU NEED TO WORK ON THIS")}
              organization={organization}
              setDialog={setDialog}
              token={token}
            />
          </Suspense>
        );


      default:
        return;
    }
  };

  function toolBarButtons() {
    return (
      <Grid
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: "2rem",
        }}
      >
        <HoverIconButton
          cypressId="facilities-btn-create-facility"
          icon={<AddIcon />}
          iconDirection="right"
          handleClick={() =>
            setDialog({
              ...dialog,
              dialogShow: true,
              dialogTitle: "Create Facility",
            })
          }
          text="Create Facility"
        />

        <HoverIconButton
          cypressId="facilities-btn-import-facilities"
          icon={<AssignmentReturnedIcon />}
          iconDirection="right"
          handleClick={() =>
            setDialog({
              ...dialog,
              dialogTitle: "Import Facilities",
              dialogShow: true,
            })
          }
          text="Import Facilities"
        />

        <HoverIconButton
          cypressId="facilities-btn-map"
          icon={<MapIcon />}
          iconDirection="right"
          handleClick={() =>
            setView({
              viewFacilitiesMap: true,
            })
          }
          text="Map"
        />
      </Grid>
    );
  }

  return (
    <PageLayout
      title="Facilities"
      subtitle="Manage locations, zones, and facility configurations"
      icon={BusinessIcon}
    >
      {redirect.isRedirect ? (
        <Redirect
          to={{
            pathname: "/facilities/overview",
            state: { selectedFacility: redirect.selectedFacility },
          }}
        />
      ) : null}
      <ModalDialog
        handleClose={dialogClose}
        open={dialogShow}
        title={`${dialogTitle}${selectedLocation?.name ? ` - ${selectedLocation?.name}` : ""
          }`}
        content={switchDialog(dialogTitle)}
      />

      {/* Table */}
      <Grid size={12}>
        <FacilityTable
          diaog={dialog}
          enableFeatures={enableFeatures}
          permissions={permissions}
          setDialog={setDialog}
          setRedirect={setRedirect}
          setView={setView}
          state={state}
          toolBarButtons={toolBarButtons}
        />
      </Grid>
    </PageLayout>
  );
}
